import { ACETATE_LIBRARY, type AcetateFamily, type AcetateSpec } from '../config/acetates';
import { ACETATE_CHIP_IDS, ACETATE_CHIP_SPRITE } from './acetateChipSprite';

/**
 * A110V acetate library chips: pure helpers for the « Couleurs → Acétate » chips
 * (sprite cells, family groups, labels, strip spacing, arrow-key moves). No DOM here.
 */

/** Family order of the desktop grid (the library is already sorted this way). */
export const ACETATE_FAMILY_ORDER: readonly AcetateFamily[] = ['essentials', 'tortoise', 'transparent', 'horn', 'fantasy'];

export interface ChipCell { index: number; col: number; row: number }

/** Cell of an acetate in the chip sprite, or null when the sprite has no chip for it (yet). */
export function chipCell(id: string): ChipCell | null {
  const index = ACETATE_CHIP_IDS.indexOf(id);
  if (index < 0) return null;
  return { index, col: index % ACETATE_CHIP_SPRITE.columns, row: Math.floor(index / ACETATE_CHIP_SPRITE.columns) };
}

/** CSS `background-size` / `background-position` (CSS px) showing one chip face of the sprite. */
export function chipSpriteStyle(id: string): { size: string; position: string } | null {
  const cell = chipCell(id);
  if (!cell) return null;
  const { columns, rows, cellPx: [cw, ch], gutterPx, scale } = ACETATE_CHIP_SPRITE;
  const px = (v: number) => `${+(v / scale).toFixed(3)}px`;
  return {
    size: `${px(columns * cw)} ${px(rows * ch)}`,
    position: `${px(-(cell.col * cw + gutterPx))} ${px(-(cell.row * ch + gutterPx))}`,
  };
}

/** The library grouped by family, in library order inside each family. */
export function acetateGroups(library: readonly AcetateSpec[] = ACETATE_LIBRARY): Array<{ family: AcetateFamily; acetates: AcetateSpec[] }> {
  return ACETATE_FAMILY_ORDER
    .map(family => ({ family, acetates: library.filter(a => a.family === family) }))
    .filter(g => g.acetates.length > 0);
}

/** « Acétate · Jaguar »: chip accessible name, tooltip and section title. */
export function chipLabel(section: string, name: string): string {
  return `${section} · ${name}`;
}

export interface PeekOptions { pad: number; chip: number; peek: number; min: number; max: number }
const PEEK: PeekOptions = { pad: 14, chip: 40, peek: 20, min: 6, max: 14 };

/**
 * Gap of the mobile strip such that the last visible chip is cut about in half at the right
 * edge (the classic « there is more » hint), whatever the panel width. Whole pixels, at least
 * `min` (the 44 px hit areas of two neighbours never overlap), at most `max`.
 */
export function peekGap(stripWidth: number, options: Partial<PeekOptions> = {}): number {
  const o = { ...PEEK, ...options };
  const room = stripWidth - o.pad - o.peek;
  const n = Math.floor(room / (o.chip + o.min));
  if (!(n > 0)) return o.min;
  return Math.max(o.min, Math.min(o.max, Math.floor(room / n - o.chip)));
}

export interface ChipRect { x: number; y: number; w: number; h: number }

/**
 * Roving focus among chips laid out in a strip or a wrapped grid. Left/Right: previous/next;
 * Home/End: first/last; Up/Down: the nearest chip in the row above/below (in a single-row
 * strip: previous/next). Returns the index to focus (possibly the same one at an end), or
 * null for any other key.
 */
export function chipNeighbour(key: string, index: number, rects: readonly ChipRect[]): number | null {
  const last = rects.length - 1;
  const cur = rects[index];
  if (!cur || last < 0) return null;
  switch (key) {
    case 'ArrowLeft': return Math.max(0, index - 1);
    case 'ArrowRight': return Math.min(last, index + 1);
    case 'Home': return 0;
    case 'End': return last;
    case 'ArrowUp':
    case 'ArrowDown': {
      const down = key === 'ArrowDown';
      const cy = cur.y + cur.h / 2;
      const cx = cur.x + cur.w / 2;
      const rows = rects.some(r => Math.abs(r.y + r.h / 2 - cy) > cur.h / 2);
      if (!rows) return down ? Math.min(last, index + 1) : Math.max(0, index - 1);
      // Chips beyond half a chip height in that direction; the nearest row of them; the closest in x.
      const ahead = rects.map((r, i) => ({ i, dy: (r.y + r.h / 2 - cy) * (down ? 1 : -1), dx: Math.abs(r.x + r.w / 2 - cx) }))
        .filter(c => c.dy > cur.h / 2);
      if (!ahead.length) return index;
      const row = Math.min(...ahead.map(c => c.dy));
      const inRow = ahead.filter(c => c.dy - row <= cur.h / 2);
      return inRow.reduce((a, b) => (b.dx < a.dx ? b : a)).i;
    }
    default: return null;
  }
}
